/**
 * How long after `beforeunload` the page still counts as unloading. `beforeunload` can be
 * cancelled, so the flag clears itself if the page is still here after this.
 */
export const UNLOAD_GRACE_MS = 10_000;

const watched = new WeakSet<EventTarget>();
let unloading = false;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

function markUnloading(): void {
  unloading = true;
  clearTimeout(resetTimer);
  resetTimer = setTimeout(() => { unloading = false; }, UNLOAD_GRACE_MS);
}

function markShown(): void {
  unloading = false;
  clearTimeout(resetTimer);
}

/**
 * Starts tracking whether the page is being navigated away from or reloaded. While it is, the
 * browser cancels in-flight requests, which then fail with status 0 even though nothing is wrong.
 * Safe to call more than once. Specs pass their own target, since Karma fails on a real `beforeunload`.
 */
export function watchPageUnload(target: EventTarget = globalThis): void {
  if (typeof target?.addEventListener !== 'function' || watched.has(target)) {
    return;
  }
  watched.add(target);
  target.addEventListener('beforeunload', markUnloading);
  target.addEventListener('pagehide', markUnloading);
  // Fires when the page comes back from the back/forward cache.
  target.addEventListener('pageshow', markShown);
}

export function isPageUnloading(): boolean {
  return unloading;
}

/** For specs. */
export function resetPageUnload(): void {
  markShown();
}
